import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { WorstEquipmentItem } from '../../../shared/types/worstEquipmentItem.ts';
import { compareNullsLast } from '../../../shared/utils/sortUtils.ts';
import { Dialog } from '../../components/dialog-portal/dialog.tsx';
import { InfoIcon } from '../../components/dialog-portal/infoIcon.tsx';


type WorstEquipmentColumnId = 'equipment' | 'partNumber' | 'efficiency' | 'quality';

interface WorstEquipmentSort {
    columnId: WorstEquipmentColumnId;
    direction: 'asc' | 'desc';
}

const COLLAPSED_ROW_COUNT = 10;


interface Props {
    style?: React.CSSProperties,
    worstEquipments: WorstEquipmentItem[],
}

export function WorstEquipmentsTable({
    style,
    worstEquipments,
}: Props) {
    const [sort, setSort] = useState<WorstEquipmentSort>({
        columnId: 'efficiency',
        direction: 'asc',
    });
    const [isExpanded, setIsExpanded] = useState(false);
    const [isInfoOpen, setIsInfoOpen] = useState(false);

    const sortedEquipments = useMemo(
        () => [...worstEquipments].sort((a, b) => compareNullsLast(
            getSortValue(a, sort.columnId),
            getSortValue(b, sort.columnId),
            sort.direction,
        )),
        [worstEquipments, sort],
    );
    const visibleEquipments = isExpanded
        ? sortedEquipments
        : sortedEquipments.slice(0, COLLAPSED_ROW_COUNT);
    const canExpand = sortedEquipments.length > COLLAPSED_ROW_COUNT;

    function handleSort(columnId: WorstEquipmentColumnId) {
        setSort(current => ({
            columnId,
            direction: current.columnId === columnId && current.direction === 'asc'
                ? 'desc'
                : 'asc',
        }));
    }

    function handleExpandedToggle() {
        setIsExpanded(current => !current);
    }

    function handleInfoOpen() {
        setIsInfoOpen(true);
    }

    function handleInfoClose() {
        setIsInfoOpen(false);
    }

    return (
        <>
            <table style={style ?? {}}>
                <thead>
                    <tr>
                        <SortableHeader columnId='equipment' label='Equipment' sort={sort} onSort={handleSort} />
                        <SortableHeader columnId='partNumber' label='Part number' sort={sort} onSort={handleSort} />
                        <SortableHeader columnId='efficiency' label='Efficiency' sort={sort} onSort={handleSort} />
                        <SortableHeader
                            columnId='quality'
                            label='Quality'
                            headerAction={<InfoIcon onClick={handleInfoOpen} title='Worst equipment calculations' />}
                            sort={sort}
                            onSort={handleSort}
                        />
                    </tr>
                </thead>
                <tbody>
                    {visibleEquipments.map(item => {
                        const efficiency = (item.efficiency * 100).toFixed(0) + '%';
                        const quality = item.quality ? (item.quality * 100).toFixed(0) + '%' : '';

                        return <tr key={item.equipment}>
                            <td className='textAlignLeft'>
                                <Link
                                    className='link-button--non-intrusive'
                                    to={`/machine-log#equipment=${item.equipment}`}
                                >
                                    {item.equipmentDisplayName}
                                </Link>
                            </td>

                            <td className='textAlignLeft'>{item.partDisplayName}</td>
                            <td>{efficiency}</td>
                            <td>{quality}</td>
                        </tr>
                    })}
                </tbody>
                {canExpand && (
                    <tfoot>
                        <tr>
                            <td colSpan={4}>
                                <button
                                    type='button'
                                    aria-expanded={isExpanded}
                                    onClick={handleExpandedToggle}
                                >
                                    {isExpanded ? 'Show less' : 'Show all'}
                                </button>
                            </td>
                        </tr>
                    </tfoot>
                )}
            </table>
            {isInfoOpen && <WorstEquipmentsInfoDialog onClose={handleInfoClose} />}
        </>
    );
}


interface HeaderProps {
    columnId: WorstEquipmentColumnId;
    label: string;
    headerAction?: React.ReactNode;
    sort: WorstEquipmentSort;
    onSort: (columnId: WorstEquipmentColumnId) => void;
}

function SortableHeader({
    columnId,
    label,
    headerAction,
    sort,
    onSort,
}: HeaderProps) {
    const isSorted = sort.columnId === columnId;
    const ariaSort = isSorted
        ? sort.direction === 'asc' ? 'ascending' : 'descending'
        : 'none';

    const sortButton = (
        <button
            type='button'
            className='table-sort-button'
            onClick={() => onSort(columnId)}
        >
            <span>{label}</span>
            {isSorted && (
                <span
                    className={`table-sort-indicator table-sort-indicator--${sort.direction}`}
                    aria-hidden='true'
                />
            )}
        </button>
    );

    return (
        <th aria-sort={ariaSort}>
            {headerAction ? (
                <span className='table-sort-header-with-action'>
                    <span className='table-sort-header-with-action__spacer' aria-hidden='true' />
                    {sortButton}
                    <span className='table-sort-header-with-action__action'>
                        {headerAction}
                    </span>
                </span>
            ) : sortButton}
        </th>
    );
}

function WorstEquipmentsInfoDialog({ onClose }: { onClose: () => void }) {
    return (
        <Dialog title='Equipment efficiency' onClose={onClose}>
            <h3>Period used</h3>
            <p>
                Calculations use a rolling 7-day period. If the current batch started less than 7 days ago, calculations begin at the batch start
                instead.
            </p>
            <p>
                Only weekday work shifts from 06:00 to 22:00 are included. Night shifts and weekends are excluded. Calculation starts after setup
                and ends at the latest completed part.
            </p>
            <p>Equipment is shown only after at least 8 hours of included active time.</p>

            <h3>Efficiency and quality</h3>
            <p>Efficiency = median cycle time × good parts ÷ included production time.</p>
            <p>
                Efficiency already includes quality because it uses the number of good parts, not the total number of parts. Quality is shown
                separately only to help explain the efficiency result.
            </p>
            <p>Quality = good parts ÷ total parts.</p>
        </Dialog>
    );
}

function getSortValue(
    equipment: WorstEquipmentItem,
    columnId: WorstEquipmentColumnId,
): string | number | null {
    switch (columnId) {
        case 'equipment': return equipment.equipmentDisplayName;
        case 'partNumber': return equipment.partDisplayName;
        case 'efficiency': return equipment.efficiency;
        case 'quality': return equipment.quality;
    }
}
